/**
 * /akun/ubah-pin — Ubah PIN (Phase 5.4).
 *
 * 3-step PIN change flow:
 *  - Step 1: enter current PIN
 *  - Step 2: enter new PIN
 *  - Step 3: confirm new PIN
 *  - On confirm-match: PUT /v1/parent/pin (current + new)
 *  - On success: replace local session with the new one returned
 *
 * Uses the same PinBoxes + PinPad components as the setup flow.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { PinBoxes } from '@/components/parent/PinBoxes';
import { PinPad } from '@/components/parent/PinPad';
import { changePin, type ChangePinResult } from '@/lib/api/parent';
import { loadSession, saveSession } from '@/lib/parent-session';
import { getOrCreateDeviceId } from '@/lib/device-id';
import { PIN_POLICY } from '@/lib/pin-policy';

const MAX_PIN_LENGTH = PIN_POLICY.LENGTH;

type Phase = 'current' | 'new' | 'confirm' | 'submitting';

type State = {
  phase: Phase;
  pin: string;
  currentPin: string;
  newPin: string;
  error: string | null;
};

const INITIAL_STATE: State = {
  phase: 'current',
  pin: '',
  currentPin: '',
  newPin: '',
  error: null,
};

export default function UbahPinScreen() {
  const router = useRouter();
  const [state, setState] = useState<State>(INITIAL_STATE);
  const submittingRef = useRef(false);

  useEffect(() => {
    (async () => {
      const session = await loadSession();
      if (!session) {
        router.replace('/akun/pin' as never);
      }
    })();
  }, [router]);

  const onDigit = useCallback((digit: string) => {
    setState((prev) => {
      if (prev.phase === 'submitting') return prev;
      if (prev.pin.length >= MAX_PIN_LENGTH) return prev;
      return { ...prev, pin: prev.pin + digit, error: null };
    });
  }, []);

  const onBackspace = useCallback(() => {
    setState((prev) => {
      if (prev.phase === 'submitting') return prev;
      return { ...prev, pin: prev.pin.slice(0, -1), error: null };
    });
  }, []);

  useEffect(() => {
    if (state.pin.length !== MAX_PIN_LENGTH) return;
    if (submittingRef.current) return;
    if (state.phase === 'current') {
      // Don't submit currentPin yet — we'll need to also send newPin
      // after the user enters it. Just advance to 'new' phase.
      setState((prev) => ({
        ...prev,
        phase: 'new',
        pin: '',
        currentPin: prev.pin,
        error: null,
      }));
      return;
    }
    if (state.phase === 'new') {
      setState((prev) => ({
        ...prev,
        phase: 'confirm',
        pin: '',
        newPin: prev.pin,
        error: null,
      }));
      return;
    }
    if (state.phase === 'confirm') {
      // Verify match.
      if (state.pin !== state.newPin) {
        setState((prev) => ({
          ...prev,
          phase: 'new',
          pin: '',
          newPin: '',
          currentPin: prev.currentPin,
          error: 'PIN baru tidak cocok. Coba lagi.',
        }));
        return;
      }
      void handleSubmit(state.currentPin, state.newPin);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.pin, state.phase]);

  const handleSubmit = useCallback(async (currentPin: string, newPin: string) => {
    submittingRef.current = true;
    setState((prev) => ({ ...prev, phase: 'submitting' }));
    const session = await loadSession();
    if (!session) {
      submittingRef.current = false;
      setState({ ...INITIAL_STATE, error: 'Sesi habis. Buka Profil dan Preferensi lagi.' });
      return;
    }
    const result: ChangePinResult = await changePin(session.token, currentPin, newPin);
    submittingRef.current = false;
    if (result.ok) {
      const deviceId = getOrCreateDeviceId();
      await saveSession({
        token: result.sessionToken,
        expiresAt: result.expiresAt,
        deviceId,
      });
      setTimeout(() => router.back(), 800);
    } else {
      setState({
        ...INITIAL_STATE,
        error: result.message ?? 'Gagal mengubah PIN.',
      });
    }
  }, [router]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  const titles: Record<Phase, { title: string; subtitle: string }> = {
    current: { title: 'PIN Saat Ini', subtitle: 'Masukkan PIN Profil dan Preferensi yang aktif.' },
    new: { title: 'PIN Baru', subtitle: 'Pilih 4 digit baru (bukan 0000/1234/pola berulang).' },
    confirm: { title: 'Konfirmasi PIN Baru', subtitle: 'Masukkan PIN baru sekali lagi.' },
    submitting: { title: 'Menyimpan…', subtitle: 'Sebentar ya.' },
  };
  const { title, subtitle } = titles[state.phase];

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <View style={styles.header}>
          <Pressable
            onPress={() => router.back()}
            style={({ pressed }) => [styles.backBtn, pressed && styles.btnPressed]}
            accessibilityRole="button"
            accessibilityLabel="Kembali"
          >
            <Text style={styles.backArrow}>←</Text>
          </Pressable>
          <ThemedText type="subtitle" style={styles.headerTitle}>Ubah PIN</ThemedText>
          <View style={{ width: 40 }} />
        </View>

        <View style={styles.center}>
          <ThemedText type="subtitle" style={styles.title}>{title}</ThemedText>
          <ThemedText type="default" themeColor="textSecondary" style={styles.subtitle}>
            {subtitle}
          </ThemedText>
          <View style={styles.boxesWrap}>
            <PinBoxes length={state.pin.length} total={MAX_PIN_LENGTH} />
          </View>
          {state.error ? <Text style={styles.errorText}>{state.error}</Text> : null}
          {state.phase === 'submitting' ? (
            <ActivityIndicator size="large" color={Brand.primary} style={styles.spinner} />
          ) : (
            <View style={styles.padWrap}>
              <PinPad onDigit={onDigit} onBackspace={onBackspace} />
            </View>
          )}
        </View>
      </SafeAreaView>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e5e5',
  },
  backBtn: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  backArrow: { fontSize: 24, color: Brand.primary, fontWeight: '600' },
  headerTitle: { flex: 1, textAlign: 'center', fontSize: 18, fontWeight: '700', color: '#1A0B5C' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: Spacing.four, gap: Spacing.three },
  title: { fontSize: 20, fontWeight: '700', color: '#1A0B5C', textAlign: 'center' },
  subtitle: { fontSize: 14, textAlign: 'center', marginBottom: Spacing.two },
  boxesWrap: { marginVertical: Spacing.two },
  errorText: { color: '#e53e3e', fontSize: 13, textAlign: 'center' },
  padWrap: { marginTop: Spacing.two },
  spinner: { marginTop: Spacing.three },
  btnPressed: { opacity: 0.7 },
});
